import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router";
import { useAuthStore } from "../../stores/auth.store";
import { MessageBubble } from "../../components/messages/MessageBubble";
import { useConversationPoll } from "../../hooks/useMessages";
import * as messageApi from "../../api/message.api";
import type { MessageWithSender } from "../../api/message.api";

export function ConversationPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  const [msgs, setMsgs] = useState<MessageWithSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(
    id === "new" ? null : id ?? null
  );
  const [otherName, setOtherName] = useState(
    searchParams.get("businessName") ?? "Conversation"
  );
  const [loadKey, setLoadKey] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isNew = id === "new";
  const businessId = searchParams.get("businessId");

  // Redirect unauthenticated users
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // Load existing conversation messages
  useEffect(() => {
    if (!conversationId || isNew) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoadError(false);
    async function load() {
      try {
        const { messages: loaded } = await messageApi.getMessages(conversationId!);
        if (!cancelled) {
          // Messages come newest-first; reverse for display
          setMsgs(loaded.reverse());

          // Set other participant name from first message that isn't ours
          const other = loaded.find((m) => m.senderId !== user?.id);
          if (other) setOtherName(other.senderName);
        }

        // Mark as read
        messageApi.markRead(conversationId!).catch(() => {});
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [conversationId, isNew, user?.id, loadKey]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  // Handle new messages from polling
  const handleNewMessages = useCallback(
    (newMsgs: MessageWithSender[]) => {
      setMsgs((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const unique = newMsgs.filter((m) => !existingIds.has(m.id));
        if (unique.length === 0) return prev;
        return [...prev, ...unique];
      });
      // Mark as read
      if (conversationId) {
        messageApi.markRead(conversationId).catch(() => {});
      }
    },
    [conversationId]
  );

  // Poll for new messages
  useConversationPoll(conversationId, handleNewMessages);

  // Send message
  async function handleSend() {
    const body = inputValue.trim();
    if (!body || sending) return;

    setSending(true);
    try {
      if (!conversationId && isNew && businessId) {
        // Start new conversation
        const { conversationId: newId } = await messageApi.startConversation(
          businessId,
          body
        );
        setConversationId(newId);
        // Add the sent message to the list optimistically
        setMsgs([
          {
            id: "temp-" + Date.now(),
            conversationId: newId,
            senderId: user!.id,
            body,
            readAt: null,
            createdAt: new Date().toISOString(),
            senderName: user!.name,
            senderAvatarUrl: null,
          },
        ]);
        // Navigate to the real conversation URL
        navigate(`/messages/${newId}`, { replace: true });
      } else if (conversationId) {
        // Send to existing conversation
        const { message: sent } = await messageApi.sendMessage(conversationId, body);
        setMsgs((prev) => [
          ...prev,
          {
            ...sent,
            senderName: user!.name,
            senderAvatarUrl: null,
          },
        ]);
      }
      setInputValue("");
      // Refocus input
      inputRef.current?.focus();
    } catch {
      // Could show error toast
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="py-16 text-center">
        <p className="mb-3 text-sm text-gray-500">Failed to load conversation.</p>
        <button
          onClick={() => { setLoading(true); setLoadError(false); setLoadKey((k) => k + 1); }}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-64px)] flex-col pb-[env(safe-area-inset-bottom)]">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <Link
          to="/messages"
          aria-label="Back to messages"
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
        >
          &larr;
        </Link>
        <h1 className="truncate text-base font-semibold text-gray-900">
          {otherName}
        </h1>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-2" role="list" aria-label="Messages">
          {msgs.map((msg) => (
            <MessageBubble
              key={msg.id}
              body={msg.body}
              createdAt={msg.createdAt}
              isSent={msg.senderId === user?.id}
              senderName={msg.senderName}
            />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {msgs.length === 0 && !isNew && (
          <div className="py-12 text-center text-sm text-gray-500">
            No messages yet
          </div>
        )}

        {isNew && msgs.length === 0 && (
          <div className="py-12 text-center text-sm text-gray-500">
            Send a message to start the conversation
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-gray-900 focus:outline-none"
            style={{ maxHeight: "120px" }}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || sending}
            className="shrink-0 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50"
          >
            {sending ? "..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
